<template>
  <div class="token-input-wrapper">
    <div class="token-inputs d-flex justify-content-center">
      <input
          v-for="index in 4"
          :key="index"
          :ref="`digit${index}`"
          v-model="digits[index - 1]"
          type="text"
          inputmode="numeric"
          pattern="[0-9]*"
          maxlength="1"
          autocomplete="one-time-code"
          class="token-digit form-control"
          :class="{ 'is-invalid': hasError }"
          @input="handleInput(index)"
          @keydown="handleKeydown($event, index)"
          @paste="handlePaste"
      />
    </div>

    <div v-if="errorMessage" class="text-danger text-center mt-2 small">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'TokenInput',

  props: {
    errorMessage: {
      type: String,
      default: null
    },
    autoFocus: {
      type: Boolean,
      default: true
    }
  },

  emits: ['complete', 'update'],

  data() {
    return {
      digits: ['', '', '', ''],
      previousCode: ''
    }
  },

  computed: {
    completeCode() {
      return this.digits.join('')
    },
    hasError() {
      return !!this.errorMessage
    }
  },

  mounted() {
    if (this.autoFocus) {
      this.focusDigit(1)
    }
  },

  methods: {
    handleChange() {
      // Only emit events if code has actually changed
      if (this.completeCode !== this.previousCode) {
        // Always emit update when code changes
        this.$emit('update', this.completeCode)

        // Emit complete only when we have 4 digits
        if (this.completeCode.length === 4) {
          this.$emit('complete', this.completeCode)
        }

        // Update previous code for next comparison
        this.previousCode = this.completeCode
      }
    },

    handleInput(index) {
      const value = this.digits[index - 1]

      // Only allow digits
      if (value && !/^\d$/.test(value)) {
        this.digits[index - 1] = ''
        return
      }

      // Move to next input if digit entered
      if (value && index < 4) {
        this.focusDigit(index + 1)
      }

      // Use centralized change handler
      this.handleChange()
    },

    handleKeydown(event, index) {
      // Backspace: clear current and move to previous
      if (event.key === 'Backspace') {
        if (!this.digits[index - 1] && index > 1) {
          this.focusDigit(index - 1)
        }
      }
      // Arrow left: move to previous
      else if (event.key === 'ArrowLeft' && index > 1) {
        event.preventDefault()
        this.focusDigit(index - 1)
      }
      // Arrow right: move to next
      else if (event.key === 'ArrowRight' && index < 4) {
        event.preventDefault()
        this.focusDigit(index + 1)
      }
    },

    async handlePaste(event) {
      event.preventDefault()

      let text = ''
      if (!text && navigator.clipboard && navigator.clipboard.readText) {
        try {
          text = await navigator.clipboard.readText()
        } catch (e) { /* ignore */ }
      }

      if (!text && event.clipboardData && event.clipboardData.getData) {
        text = event.clipboardData.getData('text') || ''
      }

      // Se non abbiamo testo, non fare nulla (meglio che inserimenti parziali)
      if (!text) return
      const digits = text.replace(/\D/g, '').slice(0, 4).split('')

      // Fill digits using $set to ensure reactivity
      for (let i = 0; i < 4; i++) {
        this.$set(this.digits, i, digits[i] || '')
      }

      // Focus last filled digit
      const lastIndex = Math.min(digits.length, 4)
      await this.$nextTick()
      this.focusDigit(lastIndex)

      // Use centralized change handler
      this.handleChange()
    },

    focusDigit(index) {
      this.$nextTick(() => {
        const ref = this.$refs[`digit${index}`]
        if (ref && ref[0]) {
          ref[0].focus()
          ref[0].select()
        }
      })
    },

    reset() {
      this.digits = ['', '', '', '']
      this.previousCode = ''
      if (this.autoFocus) {
        this.focusDigit(1)
      }
    },

    focus() {
      this.focusDigit(1)
    }
  }
}
</script>

<style scoped>

</style>
